import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './routes/ProtectedRoute';
import { CompanyAuth } from './components/auth/CompanyAuth';
import CompanyIntakeForm from './components/company/CompanyIntakeForm';
import { Toaster } from './components/ui/toaster';

// Layouts
import CompanyLayout from './layouts/CompanyLayout';
import AdminLayout from './layouts/AdminLayout';

// Company Pages
import Dashboard from './pages/company/Dashboard';
import Timeline from './pages/company/Timeline';
import DemoGuidelines from './pages/company/DemoGuidelines';
import Benefits from './pages/company/Benefits';
import FAQs from './pages/company/FAQs';
import Links from './pages/company/Links';
import Help from './pages/company/Help';

// Admin Pages
import AdminProgress from './pages/admin/CompanyProgress';
import AdminWhitelist from './pages/admin/AdminWhitelist';
import AdminSubmissions from './pages/admin/AdminSubmissions';
import AdminLinks from './pages/admin/AdminLinks';
import AdminNumbers from './pages/admin/Numbers';

// Component to handle root route based on authentication
function RootRedirect() {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on user role
  if (user?.role === 'admin') {
    return <Navigate to="/admin/progress" replace />;
  } else if (user?.role === 'company' || user?.role === 'non-demo' || user?.role === 'sponsor') {
    // Check if intake is completed
    if (!user?.intakeCompleted) {
      return <Navigate to="/company/timeline" replace />;
    }
    return <Navigate to="/company/dashboard" replace />;
  }

  // Default fallback
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { user, updateUser } = useAuth();

  const handleIntakeComplete = (companyData) => {
    // Update user context with company information
    updateUser({ ...user, companyId: companyData._id, intakeCompleted: true });
    window.location.href = '/company/timeline';
  };

  return (
    <Routes>
      <Route path="/login" element={<CompanyAuth />} />

      <Route
        path="/company/intake"
        element={
          <ProtectedRoute allowedRoles={['company', 'non-demo', 'sponsor']}>
            <CompanyIntakeForm onComplete={handleIntakeComplete} />
          </ProtectedRoute>
        }
      />

      {/* Company Routes with Layout - Now accessible to all company roles */}
      <Route
        path="/company"
        element={
          <ProtectedRoute allowedRoles={['company', 'non-demo', 'sponsor']}>
            <CompanyLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="timeline" element={<Timeline />} />
        <Route path="guidelines" element={<DemoGuidelines />} />
        <Route path="benefits" element={<Benefits />} />
        <Route path="faqs" element={<FAQs />} />
        <Route path="links" element={<Links />} />
        <Route path="help" element={<Help />} />
        <Route index element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* Admin Routes with Layout */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="progress" element={<AdminProgress />} />
        <Route path="whitelist" element={<AdminWhitelist />} />
        <Route path="submissions" element={<AdminSubmissions />} />
        <Route path="links" element={<AdminLinks />} />
        <Route path="numbers" element={<AdminNumbers />} />
        <Route index element={<Navigate to="progress" replace />} />
      </Route>

          <Route
            path="/unauthorized"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary/20 to-accent/10">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-foreground">Unauthorized</h1>
                  <p className="mt-2 text-muted-foreground">You don't have access to this page.</p>
                  <a href="/login" className="mt-4 inline-block text-primary hover:text-primary/80">
                    Go back to login
                  </a>
                </div>
              </div>
            }
          />

      <Route path="/" element={<RootRedirect />} />

      {/* Catch-all route for 404/unauthorized - redirect to role's default page */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <Toaster />
      </Router>
    </AuthProvider>
  );
}

export default App;